import React from 'react'

import {
  getTimeOfDay,
  recordFunnelEventGA,
  NeuroFitUniversalAppStoreLink,
  NeuroFitAppleAppStoreLink,
  NeuroFitGooglePlayStoreLink,
  formatReadableUnixTimestamp,
  getCoachProfileLink,
  getAppAffiliateLink,
} from '../../utils'

import {
  joinWithAnd,
  getMetaForDysregulationQuizScore,
  getMetaForQuizSymptomResultsData,
  getMetaForQuizHabitResultsData,
  getMetaForQuizSelfCheckInResultsData,
} from '../../quizUtils'

import {
  isDysregulatedState,
  getDisplayNameForState,
} from '../../constants/nervousSystemStates'

import DownloadButtonsSection from './DownloadButtonsSection'

import {
  AppleSvg,
  AndroidSvg,
  NeuroFitNamedLogoSvg,
} from '../../constants/svgs'

import HrvResultsPanel from './HrvResultsPanel'
import PieChartInsightSection from './PieChartInsightSection'
import HabitBarChartInsightSection from './HabitBarChartInsightSection'

import {
  REQUEST_SUCCESS,
} from '../../constants/requestStates'

import {
  GA_LABEL_QUIZ_CLICKED_DOWNLOAD_APP_BUTTON,
  GA_LABEL_QUIZ_CLICKED_COACH_PROFILE_BUTTON,
} from '../../constants/gaEventLabels'

const DysregulationQuizResults = ({
  safeAreaInsets,
  quizResultState,
  quizResults,
  isIOSDevice,
  isAndroidDevice,
  logAnalytics,
  showTimestamp,
  isNative,
  isSubscribedUser,
  onExitButtonClick,
  userFirstName,
  currentMood,
}) => {
  const timeOfDay = getTimeOfDay()
  const quizCompleted = (quizResultState === REQUEST_SUCCESS)
  const firstName = (quizCompleted && quizResults.meta && quizResults.meta.first_name) || userFirstName
  const resultsCreatedAtUnix = quizCompleted &&  quizResults.meta && quizResults.meta.created_at_unix

  const overallQuizResultScore = quizCompleted && quizResults.score.overall * 100.0

  const quizSymptomResults = quizCompleted && quizResults.score.symptoms
  const quizSymptomResultsData = quizCompleted && [
    {
      name: "overall",
      value: quizSymptomResults.total
    },
    {
      name: "physical",
      value: quizSymptomResults.physical
    },
    {
      name: "mental",
      value: quizSymptomResults.mental
    },
    {
      name: "emotional",
      value: quizSymptomResults.emotional
    },
  ]

  const quizHabitResults = quizCompleted && quizResults.score.habits
  const quizHabitResultsData = quizCompleted && [
    {
      name: "overall",
      value: quizHabitResults.total
    },
    {
      name: "embody",
      value: quizHabitResults.embody
    },
    {
      name: "prevent",
      value: quizHabitResults.prevent
    },
    {
      name: "nourish",
      value: quizHabitResults.nourish
    },
  ]

  const quizCheckInResultsData = quizCompleted && quizResults.score.life_aspects.breakdown

  let topSymptomsList = []
  quizHabitResultsData && quizHabitResultsData.filter(q => q.name !== "overall").map(q => {
    if (q.value <= 0.5) {
      topSymptomsList.push({name: q.name + " habits", value: q.value})
    }
  })
  quizSymptomResultsData && quizSymptomResultsData.filter(q => q.name !== "overall").map(q => {
    if (q.value <= 0.5) {
      topSymptomsList.push({name: q.name + " symptoms", value: q.value})
    }
  })
  if (quizCompleted && quizResults.score.life_aspects.total <= 0.5) {
    topSymptomsList.push({name: "overall mood", value: quizResults.score.life_aspects.total})
  }
  topSymptomsList.sort((a, b) => a.value - b.value)
  if (topSymptomsList.length > 3) {
    topSymptomsList = topSymptomsList.slice(0, 3)
  }

  const ctaText = topSymptomsList.length > 0 ? "resolve your " + joinWithAnd(topSymptomsList.map(t => t.name)) : "optimize your nervous system and feel + perform your best"

  const valuePropBulletPoints = [
    "Daily Self Check-Ins that build nervous system awareness",
    "In-App HRV measurements to measure objective results",
    "Personalized Exercises & Insights that create nervous system resilience"
  ]

  const isCoachingPage = (!!quizResults.coach_details)
  const coachUsername = isCoachingPage && quizResults.coach_details.username
  const coachFirstName = isCoachingPage && quizResults.coach_details.first_name
  const affiliateCode = isCoachingPage && quizResults.coach_details.affiliate_code

  const appDownloadLink = isCoachingPage ? getAppAffiliateLink(affiliateCode) : (isIOSDevice ? NeuroFitAppleAppStoreLink : (isAndroidDevice ? NeuroFitGooglePlayStoreLink : NeuroFitUniversalAppStoreLink))

  return (
    <div style={{padding: "0px 30px", marginBottom: safeAreaInsets.bottom + 30, backgroundColor: "transparent"}}>
      <div style={{fontSize: "min(7.5vw, 32px)", lineHeight: "min(7.5vw, 32px)", fontFamily: "Futura"}}>
        {"YOUR QUIZ RESULTS"}
      </div>
      {showTimestamp && (
        <div style={{fontSize: "min(5.5vw, 22px)", lineHeight: "min(7.5vw, 32px)", fontFamily: "Futura", margin: "2vh 0vh"}}>
          {`${formatReadableUnixTimestamp(resultsCreatedAtUnix).toUpperCase()}`}
        </div>
      )}
      <div style={{fontSize: "min(6vw, 26px)", lineHeight: "min(7.5vw, 32px)", fontFamily: "Futura Light", marginTop: "4vh", marginBottom: "4vh"}}>
        {`${firstName}, your Discovery Quiz results are below.`}
      </div>
      <HrvResultsPanel
        title={"OVERALL NERVOUS SYSTEM REGULATION SCORE"}
        marginTop={"min(2vh, 8px)"}
        value={overallQuizResultScore}
        dataIsAvailable
        isPercentage
        valueDecimalPoints={0}
        isIncreasingStatistic
        valueMetaObject={getMetaForDysregulationQuizScore(overallQuizResultScore, getTimeOfDay(resultsCreatedAtUnix), timeOfDay, firstName)}
        baselineAvailable={false}
        backgroundColor={getMetaForDysregulationQuizScore(overallQuizResultScore, getTimeOfDay(resultsCreatedAtUnix), timeOfDay, firstName).color}
        valueBaseline={undefined}
        valueBaselineStdDev={undefined}
        valueDescriptorNameColor={"#000000"}
        hideInfoButton
      />
      <HabitBarChartInsightSection
        title={`SYMPTOM BALANCE`}
        dataIsAvailable
        data={quizSymptomResultsData}
        insightText={getMetaForQuizSymptomResultsData(quizSymptomResultsData, getTimeOfDay(resultsCreatedAtUnix), firstName)}
        firstName={firstName}
      />
      <HabitBarChartInsightSection
        title={`HABIT CONSISTENCY`}
        dataIsAvailable
        data={quizHabitResultsData}
        insightText={getMetaForQuizHabitResultsData(quizHabitResultsData, getTimeOfDay(resultsCreatedAtUnix), firstName)}
        firstName={firstName}
      />
      <PieChartInsightSection
        title="SELF CHECK-IN"
        disableDropDown
        dataIsAvailable
        data={quizCheckInResultsData}
        insightText={getMetaForQuizSelfCheckInResultsData(quizCheckInResultsData, firstName)}
      />
      {isCoachingPage ? (
        <div>
          <div style={{fontSize: "min(7.5vw, 32px)", lineHeight: "min(7.5vw, 32px)", marginTop: "6vh", fontFamily: "Futura"}}>
            {"NEXT STEPS"}
          </div>
          <div style={{fontSize: "min(6vw, 26px)", fontFamily: "Futura Book", marginTop: "3vh", marginBottom: "3vh"}}>
            {`Your Coach ${coachFirstName} will respond to your quiz results within 1-2 business days.`}
          </div>
          <div style={{fontSize: "min(6vw, 26px)", fontFamily: "Futura Book", marginBottom: "3vh"}}>
            {`To make the most of your coaching, get started on the NEUROFIT App as well:`}
          </div>
          <div style={{display: "flex", justifyContent: "center"}}>
            <button
              style={{display: "flex", alignItems: "top", justifyContent: "center", width: "100%", maxWidth: 400, textAlign: "center", fontFamily: "Futura", fontSize: "min(6vw, 25px)", lineHeight: "min(6vw, 25px)", marginTop: 32, color: "#ffffff", backgroundColor: "#000000", padding: "18px 15px", border: "none"}}
              onClick={() => {
                if (logAnalytics) {
                  recordFunnelEventGA(GA_LABEL_QUIZ_CLICKED_DOWNLOAD_APP_BUTTON)
                }
                window.open(appDownloadLink, "_blank", "noopener noreferrer nofollow")
              }}
            >
              {isIOSDevice ? (
                <AppleSvg />
              ) : (isAndroidDevice ? (
                <AndroidSvg />
              ) : null)}
              <span>
                {"DOWNLOAD THE APP"}
              </span>
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{fontSize: "min(6vw, 26px)", fontFamily: "Futura Light", marginTop: "3vh", marginBottom: "3vh"}}>
            {`To ${ctaText}, download the NEUROFIT app.`}
          </div>
          <div style={{fontSize: "min(6vw, 26px)", fontFamily: "Futura Light", marginBottom: "3vh"}}>
            {`In our guided 6-week program, you'll re-balance your nervous system through:`}
          </div>
          {valuePropBulletPoints.map(bulletPoint => (
            <div key={bulletPoint} style={{display: "flex", alignItems: "top", justifyContent: "start", marginTop: "0.4em"}}>
              <div style={{display: "inline-block", fontSize: "min(7vw, 30px)", lineHeight: "min(7vw, 30px)", fontFamily: "Futura Medium", marginInlineEnd: "0.6em"}}>
                {"•"}
              </div>
              <div style={{display: "inline-block", fontSize: "min(6vw, 26px)", lineHeight: "min(7vw, 30px)", fontFamily: "Futura Light"}}>
                {bulletPoint}
              </div>
            </div>
          ))}
          <div style={{display: "flex", justifyContent: "center"}}>
            <button
              style={{display: "flex", alignItems: "top", justifyContent: "center", width: "100%", maxWidth: 400, textAlign: "center", fontFamily: "Futura", fontSize: "min(6vw, 25px)", lineHeight: "min(6vw, 25px)", marginTop: 32, color: "#ffffff", backgroundColor: "#000000", padding: "18px 15px", border: "none"}}
              onClick={() => {
                if (logAnalytics) {
                  recordFunnelEventGA(GA_LABEL_QUIZ_CLICKED_DOWNLOAD_APP_BUTTON)
                }
                window.open(appDownloadLink, "_blank", "noopener noreferrer nofollow")
              }}
            >
              {isIOSDevice ? (
                <AppleSvg />
              ) : (isAndroidDevice ? (
                <AndroidSvg />
              ) : null)}
              <span>
                {"DOWNLOAD THE APP"}
              </span>
            </button>
          </div>
        </div>
      )}
      <div style={{marginTop: "max(6vw, 32px)", paddingBottom: `calc(max(6vw, 32px) + ${safeAreaInsets.bottom}px)`, display: "flex", justifyContent: "center"}}>
        <NeuroFitNamedLogoSvg
          logoSize={"min(4.25vw, 18px)"}
          color={"#CFD2DD"}
        />
      </div>
    </div>
  )
}

export default DysregulationQuizResults;